/**
 * @fileoverview Zod schema for the set-password form.
 * @description Runs before the accept-invitation request is sent so obviously-invalid input never
 * reaches the API layer. Password complexity is still enforced server-side, but the schema now
 * mirrors the live GET /identity/password-policy response (via passwordMeetsPolicy) rather than
 * only checking presence — a password the visible rule checklist shows as non-compliant must also
 * fail submit, not just look wrong. The confirm-password field only exists client-side for typo
 * protection; it's never sent to the API. Every issue message is a translation key (per this
 * repo's i18n rule for validators), translated only at the display call site in SetPasswordForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { z } from 'zod'

import { passwordMeetsPolicy } from '@/features/auth/utils/password-policy.utils'

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

/**
 * @description Builds the set-password schema against the currently fetched password policy, so
 * submit-time validation never accepts a password the live checklist would still show as
 * non-compliant. Before the policy has loaded, only the required/match checks apply — matching
 * the checklist's own "nothing to show yet" state — since there's no policy to validate against.
 * @param {PasswordPolicy | undefined} policy - The fetched password policy, once loaded.
 * @returns {z.ZodType<{ newPassword: string; confirmPassword: string }>} The schema to resolve
 * the form against.
 */
export function buildSetPasswordFormSchema(policy: PasswordPolicy | undefined) {
  return z
    .object({
      newPassword: z.string().min(1, 'acceptInvitation.errors.newPasswordRequired'),
      confirmPassword: z.string().min(1, 'acceptInvitation.errors.confirmPasswordRequired'),
    })
    .superRefine((values, ctx) => {
      if (
        policy &&
        values.newPassword.length > 0 &&
        !passwordMeetsPolicy(policy, values.newPassword)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['newPassword'],
          message: 'acceptInvitation.errors.passwordPolicyNotMet',
        })
      }

      if (values.confirmPassword.length > 0 && values.newPassword !== values.confirmPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: 'acceptInvitation.errors.passwordsDontMatch',
        })
      }
    })
}

export type SetPasswordFormValues = z.infer<ReturnType<typeof buildSetPasswordFormSchema>>
